import { useQuery } from '@tanstack/react-query';
import { getMyChannels, getChannelMembers } from '@/services/channelApi';
import { useAuth } from '@/contexts/AuthContext';
import type { ChannelMember } from '@/types/channel';

/**
 * Hook to fetch channels owned by or accessible to the current user.
 * Query key includes user.id to isolate caches when users switch accounts.
 * Query is disabled when unauthenticated (no user.id).
 */
export function useMyChannels() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['my-channels', user?.id],
    queryFn: getMyChannels,
    enabled: !!user?.id,
  });
}

/**
 * Hook to fetch all athlete members across all of the current coach's channels.
 * Returns a deduplicated list of ChannelMember objects.
 *
 * Enabled only when channelSlugs is non-empty.
 *
 * @param channelSlugs - slugs of channels to fetch members from
 * @param excludeUserIds - user IDs to omit from the result (e.g. [currentUser.id]
 *   prevents the coach from appearing in their own dropdown when they are also a
 *   channel member)
 */
export function useCoachAthletes(channelSlugs: string[], excludeUserIds: string[] = []) {
  return useQuery({
    queryKey: ['coach-athletes', channelSlugs, excludeUserIds],
    enabled: channelSlugs.length > 0,
    queryFn: async (): Promise<ChannelMember[]> => {
      const exclude = new Set(excludeUserIds);
      // F-2 (red team): GET /channels/{slug}/members is owner/channel_admin-only.
      // A coach can be passed a channel they only belong to (not own) — e.g. by
      // a caller that has not filtered to owned channels — and that single 403
      // must not empty the whole picker via Promise.all's reject-on-first-error
      // behaviour. Promise.allSettled + keep only the fulfilled slugs.
      const settled = await Promise.allSettled(
        channelSlugs.map((slug) => getChannelMembers(slug))
      );
      // Flatten + deduplicate by user_id; skip explicitly excluded IDs.
      const seen = new Set<string>();
      const athletes: ChannelMember[] = [];
      for (const result of settled) {
        if (result.status !== 'fulfilled') continue;
        for (const member of result.value) {
          if (exclude.has(member.user_id)) continue;
          if (!seen.has(member.user_id)) {
            seen.add(member.user_id);
            athletes.push(member);
          }
        }
      }
      return athletes;
    },
  });
}

/**
 * Hook to fetch all coach/admin members across the current athlete's channels.
 * Returns a deduplicated list of ChannelMember objects filtered to role === 'coach'
 * or 'admin'. Used by athletes to populate the "Share with coach" dropdown.
 *
 * Enabled only when channelSlugs is non-empty.
 *
 * @param channelSlugs - slugs of channels to fetch members from
 */
export function useAthleteCoaches(channelSlugs: string[]) {
  return useQuery({
    queryKey: ['athlete-coaches', channelSlugs.slice().sort()],
    enabled: channelSlugs.length > 0,
    queryFn: async (): Promise<ChannelMember[]> => {
      const results = await Promise.all(
        channelSlugs.map((slug) => getChannelMembers(slug))
      );
      const seen = new Set<string>();
      const coaches: ChannelMember[] = [];
      for (const members of results) {
        for (const member of members) {
          if (member.role !== 'coach' && member.role !== 'admin') continue;
          if (!seen.has(member.user_id)) {
            seen.add(member.user_id);
            coaches.push(member);
          }
        }
      }
      return coaches;
    },
  });
}
